import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';

/** Format epoch seconds (as string or number) to DD/MM/YYYY only, or dash if null. */
export const formatDate = (
  epoch: string | number | null | undefined,
): string => {
  if (!epoch) return '—';
  return new Date(Number(epoch) * 1000).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/** Compute turnaround time between two epoch-seconds timestamps as "N day(s)". */
export const computeTurnaround = (
  sent: string | number,
  completed: string | number | null | undefined,
): string => {
  if (!completed) return '—';
  const days = Math.ceil((Number(completed) - Number(sent)) / 86_400);
  return `${days} day${days !== 1 ? 's' : ''}`;
};

/** Validates that the selected range is within the last year and not in the future. */
export const isValidRange = (
  range: DateRangePickerProps.Value | null,
): DateRangePickerProps.ValidationResult => {
  if (range === null) return { valid: true };

  const now = new Date();
  now.setHours(23, 59, 59, 999); // end of today — allows today's date to pass

  const oneYearAgo = new Date();
  oneYearAgo.setHours(0, 0, 0, 0); // start of today, then shift back 1 year
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  if (range.type === 'absolute') {
    if (!range.startDate || !range.endDate) {
      return { valid: false, errorMessage: 'Select both start and end dates.' };
    }
    if (range.startDate > range.endDate) {
      return {
        valid: false,
        errorMessage: 'End date must be equal to or after start date.',
      };
    }
    if (new Date(range.startDate) < oneYearAgo) {
      return {
        valid: false,
        errorMessage: 'Start date cannot be more than 1 year in the past.',
      };
    }
    if (new Date(range.endDate) > now) {
      return {
        valid: false,
        errorMessage: 'End date cannot be in the future.',
      };
    }
  }

  if (range.type === 'relative') {
    const totalDays =
      range.unit === 'day'
        ? range.amount
        : range.unit === 'week'
          ? range.amount * 7
          : range.unit === 'month'
            ? range.amount * 30
            : range.amount * 365;
    if (totalDays > 365) {
      return {
        valid: false,
        errorMessage: 'Relative range cannot exceed 1 year.',
      };
    }
  }

  return { valid: true };
};

/** Returns a reason string if date is disabled, empty string otherwise. */
export const dateDisabledReason = (date: Date): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  if (date > today) return 'Future dates are not allowed.';
  if (date < oneYearAgo) return 'Dates older than 1 year are not allowed.';
  return '';
};

/** Returns true if the date is selectable (within last year, not future). */
export const isDateEnabled = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  return date >= oneYearAgo && date <= today;
};
